import { useCallback } from "react";
import { useSecrets } from "./useSecrets";
import { createTags, updateTagsWithUser, setTagValue } from "../utils/tagHelpers";

export function useSecretActions() {
  const { fetchSecret, createSecret, updateSecret, deleteSecret } = useSecrets();

  // Fetches the source secret's value, then creates a new secret under newName
  // at the caller-chosen target cluster/provider, with fresh Environment/Team
  // tags (this is a real creation, so CreatedBy/UpdatedBy are stamped to the
  // current user rather than copied from the source). The original secret is
  // never touched. Also used for bulk clone-to-cluster (same name, target's
  // own original Environment/Team passed through as environment/team).
  const duplicateSecret = useCallback(
    async ({
      sourceName,
      sourcePlatformName,
      sourceHeaders,
      newName,
      targetPlatformName,
      targetHeaders,
      environment,
      team,
      nameParts,
    }) => {
      const source = await fetchSecret(sourceName, sourcePlatformName, sourceHeaders);
      const tags = createTags(nameParts, environment, team, targetPlatformName);
      const secretData = {
        secretName: newName,
        secretValue: JSON.stringify(source.secretValue),
        tags,
      };
      await createSecret(secretData, targetPlatformName, targetHeaders);
      return { newName };
    },
    [fetchSecret, createSecret]
  );

  // Emulates a rename: create the secret under newName (copying value + tags,
  // with UpdatedBy refreshed), then delete oldName — ONLY after the create
  // succeeds, so a failed create never loses data. If the delete step fails,
  // the caller is told so it can surface a "clean up manually" warning instead
  // of silently losing track of the old secret.
  const renameSecret = useCallback(
    async ({ oldName, newName, platformName, headers, nameParts }) => {
      const source = await fetchSecret(oldName, platformName, headers);
      const newTags = updateTagsWithUser(source.tags, nameParts, platformName);

      const secretData = {
        secretName: newName,
        secretValue: JSON.stringify(source.secretValue),
        tags: newTags,
      };

      await createSecret(secretData, platformName, headers);

      try {
        await deleteSecret(oldName, platformName, headers);
        return { status: "renamed" };
      } catch (error) {
        return { status: "partial", error };
      }
    },
    [fetchSecret, createSecret, deleteSecret]
  );

  // Updates Environment and/or Team on an existing secret in place (no
  // duplicate/delete). Either field may be omitted ("") to leave it untouched
  // — used for bulk re-tag, where only the fields the user actually picked
  // should change across the whole selection.
  const bulkUpdateTags = useCallback(
    async ({ secretName, platformName, headers, environment, team, nameParts }) => {
      const source = await fetchSecret(secretName, platformName, headers);
      let newTags = updateTagsWithUser(source.tags, nameParts, platformName);
      if (environment) newTags = setTagValue(newTags, "Environment", environment, platformName);
      if (team) newTags = setTagValue(newTags, "Project", team, platformName);

      const payload = {
        secretValue: JSON.stringify(source.secretValue),
        tags: newTags,
      };
      await updateSecret(secretName, payload, platformName, headers);
    },
    [fetchSecret, updateSecret]
  );

  return { duplicateSecret, renameSecret, bulkUpdateTags };
}
